import { useTranslation } from 'react-i18next';

import { forwardRef, Text, VStack } from '@bonusly/design-system';

import { useActionInput } from '../../context';
import { Budget } from '../../lib/entities/budget';
import { SelectorBox, SelectorProps } from '../selector/selector-box';
import { SelectorOption } from '../selector/selector-option';
import { useSelector } from '../selector/use-selector';
import { AmountBalance } from './amount-balance';
import { FlashPointsCountdown } from './flash-points-countdown';

interface AmountSelectorProps extends SelectorProps<number> {
	allowanceBoostId?: string;
	budget: Budget;
}

export const AmountSelector = forwardRef<AmountSelectorProps, 'div'>(({ allowanceBoostId, budget: _propBudget, command, hash, items }, ref) => {
	const { t } = useTranslation('components');
	const { budget: contextBudget, context } = useActionInput();
	// Extension config is static after editor creation — use the live context budget
	// so proxy giving balance changes are reflected in the dropdown.
	const budget = contextBudget ?? _propBudget;

	const { getOptionProps, getSelectorProps, selectedIndex } = useSelector({
		command,
		hash,
		items,
		ref,
	});

	const isAwardContext = context.includes('award');

	// When the balance is 0 there are no selectable amounts, but a point giver
	// should still see the popup so it can surface the "add to balance" points boost prompt.
	// Delegates with no balance don't see the message since they
	// can't top up someone else's balance.
	const showNoBalanceMessage = budget?.hasAllowance && !budget.data.balance && !budget.data.delegateName;

	if (!items.length && !showNoBalanceMessage) {
		return null;
	}

	return (
		<SelectorBox minW="auto">
			{!isAwardContext && <FlashPointsCountdown />}
			<VStack spacing={0} align="stretch" {...getSelectorProps()}>
				{budget.hasAllowance && <AmountBalance allowanceBoostId={allowanceBoostId} budget={budget} />}

				{items.map((amount, index) => (
					<SelectorOption
						key={amount}
						isSelected={selectedIndex === index}
						aria-label={t('rich_media.amount.select_amount', { amount })}
						_hover={{ backgroundColor: 'points.bgAlpha' }}
						sx={{
							'&[data-focus="true"]': {
								backgroundColor: 'points.bgAlpha',
							},
						}}
						{...getOptionProps(index)}
					>
						<Text fontSize="md" fontWeight="medium" lineHeight={6} color="points.fg">
							+{budget.format(amount)}
						</Text>
					</SelectorOption>
				))}
			</VStack>
		</SelectorBox>
	);
});
